export type InsightTone = 'up' | 'down' | 'good' | 'warn' | 'neutral';
export interface Insight {
  tone: InsightTone;
  text: string;
}
export interface InsightsInput {
  kpiData: { totalIncome: number; totalExpense: number; netIncome: number };
  previousYearKpiData: { totalIncome: number; totalExpense: number; netIncome: number };
  monthlyData: { month: number; income: number; expense: number; netIncome: number }[];
  categoryExpenses: { category: string; amount: number; percentage: number }[];
}

const won = (n: number): string => `${Math.round(Math.abs(n)).toLocaleString('ko-KR')}`;

/** raw(반올림 전) 퍼센트로 ±5% 임계를 판정하고, 임계를 넘으면 반올림된 표시값을 함께 반환한다. */
const classifyChange = (from: number, to: number): { direction: 'up' | 'down'; displayPct: number } | null => {
  const rawPct = ((to - from) / from) * 100;
  if (rawPct >= 5) return { direction: 'up', displayPct: Math.round(rawPct) };
  if (rawPct <= -5) return { direction: 'down', displayPct: Math.round(Math.abs(rawPct)) };
  return null;
};

/** 규칙 기반 인사이트 생성. 결정적(같은 입력 → 같은 출력), 외부 상태·랜덤 없음. */
export function generateInsights(input: InsightsInput): Insight[] {
  const out: Insight[] = [];

  // 1. 직전월 대비 지출 증감 (데이터 있는 마지막 active 달과, 그 바로 전달(연속 월)만 비교, ±5% 임계)
  const active = input.monthlyData.filter((m) => m.income !== 0 || m.expense !== 0);
  if (active.length >= 1) {
    const cur = active[active.length - 1];
    const prev = input.monthlyData.find((m) => m.month === cur.month - 1);
    if (prev && prev.expense > 0) {
      const change = classifyChange(prev.expense, cur.expense);
      if (change?.direction === 'up') out.push({ tone: 'down', text: `지난달보다 지출이 ${change.displayPct}% 늘었어요` });
      else if (change?.direction === 'down') out.push({ tone: 'up', text: `지난달보다 지출이 ${change.displayPct}% 줄었어요` });
    }
  }

  // 2. 가장 많이 쓴 카테고리
  const topCat = [...input.categoryExpenses].sort((a, b) => b.amount - a.amount)[0];
  if (topCat && topCat.amount > 0) {
    out.push({ tone: 'neutral', text: `이번 기간 가장 많이 쓴 건 ${topCat.category}(${won(topCat.amount)})예요` });
  }

  // 3. 손익 상태
  const net = input.kpiData.netIncome;
  if (net > 0) out.push({ tone: 'good', text: `이번 기간은 ${won(net)} 남았어요` });
  else if (net < 0) out.push({ tone: 'warn', text: `이번 기간은 ${won(net)} 모자랐어요` });

  // 4. 매출 추세 (작년 동기 대비)
  const curRev = input.kpiData.totalIncome;
  const prevRev = input.previousYearKpiData.totalIncome;
  if (prevRev > 0 && curRev > 0) {
    const change = classifyChange(prevRev, curRev);
    if (change?.direction === 'up') out.push({ tone: 'up', text: `매출이 작년보다 ${change.displayPct}% 늘었어요` });
    else if (change?.direction === 'down') out.push({ tone: 'down', text: `매출이 작년보다 ${change.displayPct}% 줄었어요` });
  }

  return out.slice(0, 3);
}
